import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Tabs, Card, Tag, Spin, Empty, Typography, Button, Descriptions, message as antdMessage, Progress,
} from 'antd';
import {
  PictureOutlined, FileOutlined, VideoCameraOutlined, SoundOutlined,
  FileTextOutlined, DownloadOutlined, LeftOutlined, RightOutlined, CloseOutlined,
  UploadOutlined, LoadingOutlined,
} from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import { TimezoneUtils } from '../utils/timezoneUtils';
import { getEFormApprovalLabels } from '../utils/eformApprovalLabels';

const { Text } = Typography;

// ─── File helpers ──────────────────────────────────────────────────────────────

const getFileType = (fileName) => {
  if (!fileName) return 'file';
  const ext = (String(fileName).split('.').pop() || '').toLowerCase().trim();
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'heic', 'heif'].includes(ext)) return 'image';
  if (['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'm4v'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'].includes(ext)) return 'audio';
  if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv'].includes(ext)) return 'document';
  return 'file';
};

const getFileIcon = (fileName) => {
  const type = getFileType(fileName);
  const style = { fontSize: 28, color: '#8c8c8c' };
  switch (type) {
    case 'video': return <VideoCameraOutlined style={style} />;
    case 'audio': return <SoundOutlined style={style} />;
    case 'document': return <FileTextOutlined style={style} />;
    default: return <FileOutlined style={style} />;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Pending':   return 'orange';
    case 'Approved':  return 'green';
    case 'Rejected':  return 'red';
    case 'Submitted': return 'blue';
    default:          return 'default';
  }
};

const getFillTypeColor = (fillType) => {
  switch (fillType) {
    case 'Manual':    return 'blue';
    case 'AI':        return 'green';
    case 'MetaFlows': return 'purple';
    case 'Data':      return 'cyan';
    default:          return 'default';
  }
};

// ─── Received Media sidebar ────────────────────────────────────────────────────

const ReceivedMediaPanel = ({ attachments, loading, onOpenLightbox, t }) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '24px 0' }}>
        <Spin size="small" />
      </div>
    );
  }

  if (!attachments || attachments.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('workflowMonitor.noMediaFiles')}
        style={{ margin: '16px 0' }}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {attachments.map((file, idx) => {
        const displayName = file.originalFileName || file.name || file.fileName || '';
        const fileType = getFileType(displayName);
        const isImage = fileType === 'image';
        const isVideo = fileType === 'video';
        const canPreview = isImage || isVideo;

        return (
          <Card
            key={file.id || idx}
            size="small"
            styles={{ body: { padding: '8px' } }}
            style={{ borderRadius: 8, cursor: canPreview ? 'pointer' : 'default' }}
            hoverable={canPreview}
            onClick={() => canPreview && onOpenLightbox(file, attachments)}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {/* Thumbnail */}
              <div style={{
                width: 56, height: 56, flexShrink: 0,
                backgroundColor: '#f0f0f0', borderRadius: 6,
                overflow: 'hidden', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}>
                {isImage ? (
                  <img
                    src={file.filePath}
                    alt={displayName}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                    }}
                  />
                ) : (
                  getFileIcon(displayName)
                )}
              </div>

              {/* File info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text
                  style={{ fontSize: 12, display: 'block', wordBreak: 'break-all', lineHeight: 1.3 }}
                  title={displayName}
                >
                  {displayName.length > 30 ? `${displayName.substring(0, 30)}…` : displayName}
                </Text>
                {file.createdAt && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {new Date(file.createdAt).toLocaleDateString('zh-TW')}
                  </Text>
                )}
              </div>

              {/* Download */}
              <a
                href={file.filePath}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <Button icon={<DownloadOutlined />} size="small" type="text" />
              </a>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

// ─── Form Basic Information panel ─────────────────────────────────────────────

const FormBasicInfoPanel = ({ instance, userTimezoneOffset, processUserMessage, t }) => {
  if (!instance) return null;

  const approvalLabels = getEFormApprovalLabels(instance, t, {
    approve: 'workflowMonitor.eformStatusApproved',
    reject:  'workflowMonitor.eformStatusRejected',
  });

  const getStatusText = (status) => {
    switch (status) {
      case 'Pending':   return t('workflowMonitor.eformStatusPending');
      case 'Approved':  return approvalLabels.approve;
      case 'Rejected':  return approvalLabels.reject;
      case 'Submitted': return t('workflowMonitor.eformStatusSubmitted');
      default: return status;
    }
  };

  const rawUserInput = instance.userMessage || instance.userInput;
  const processedUserInput = processUserMessage ? processUserMessage(rawUserInput) : rawUserInput;

  const descItems = [
    {
      key: 'formName',
      label: t('workflowMonitor.formName'),
      children: <Text strong>{instance.formName || '—'}</Text>,
      span: 2,
    },
    {
      key: 'status',
      label: t('workflowMonitor.status'),
      children: instance.status ? (
        <Tag color={getStatusColor(instance.status)}>{getStatusText(instance.status)}</Tag>
      ) : '—',
    },
    {
      key: 'fillType',
      label: t('workflowMonitor.fillType'),
      children: instance.fillType ? (
        <Tag color={getFillTypeColor(instance.fillType)}>{instance.fillType}</Tag>
      ) : '—',
    },
    {
      key: 'instanceName',
      label: t('workflowMonitor.instanceName'),
      children: <Text copyable={!!instance.instanceName}>{instance.instanceName || '—'}</Text>,
      span: 2,
    },
    ...(instance.createdAt ? [{
      key: 'createdAt',
      label: t('workflowMonitor.createdAt'),
      children: TimezoneUtils.formatDateWithTimezone(instance.createdAt, userTimezoneOffset),
    }] : []),
    ...(instance.createdBy ? [{
      key: 'createdBy',
      label: t('workflowMonitor.createdBy'),
      children: instance.createdBy,
    }] : []),
    ...(instance.approvalBy || instance.approvedBy ? [{
      key: 'approvalBy',
      label: t('workflowMonitor.approvalBy'),
      children: instance.approvalBy || instance.approvedBy,
    }] : []),
    ...(instance.approvalAt ? [{
      key: 'approvalAt',
      label: t('workflowMonitor.approvalTime'),
      children: TimezoneUtils.formatDateWithTimezone(instance.approvalAt, userTimezoneOffset),
    }] : []),
    ...(processedUserInput ? [{
      key: 'userInput',
      label: t('workflowMonitor.userInput'),
      children: typeof processedUserInput === 'string' ? (
        <div
          style={{
            padding: '8px 12px',
            backgroundColor: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: 6,
            fontSize: 14,
            lineHeight: 1.6,
            maxHeight: 200,
            overflowY: 'auto',
          }}
          dangerouslySetInnerHTML={{ __html: processedUserInput }}
        />
      ) : processedUserInput,
      span: 2,
    }] : []),
    ...(instance.approvalNote ? [{
      key: 'approvalNote',
      label: t('workflowMonitor.approvalNote'),
      children: (
        <div style={{
          padding: '8px 12px',
          backgroundColor: '#fff7e6',
          border: '1px solid #ffd591',
          borderRadius: 6,
          fontSize: 14,
          lineHeight: 1.6,
        }}>
          {instance.approvalNote}
        </div>
      ),
      span: 2,
    }] : []),
  ];

  return (
    <div style={{ padding: '8px 0' }}>
      <Descriptions
        column={2}
        bordered
        size="small"
        items={descItems}
        labelStyle={{
          whiteSpace: 'nowrap',
          minWidth: 100,
          width: 100,
          fontWeight: 500,
          color: 'rgba(0,0,0,0.65)',
          backgroundColor: '#fafafa',
        }}
      />
    </div>
  );
};

// ─── Lightbox ──────────────────────────────────────────────────────────────────

const Lightbox = ({ visible, files, currentIndex, onClose, onPrev, onNext, zIndex }) => {
  if (!visible || !files.length) return null;
  const file = files[currentIndex];
  if (!file) return null;
  const displayName = file.originalFileName || file.name || file.fileName || '';
  const isVideo = getFileType(displayName) === 'video';

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.88)',
        zIndex,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <Button
        type="text"
        icon={<CloseOutlined />}
        onClick={onClose}
        style={{
          position: 'absolute', top: 16, right: 16,
          color: '#fff', fontSize: 20,
          backgroundColor: 'rgba(255,255,255,0.1)',
        }}
      />
      {files.length > 1 && (
        <Button
          type="text"
          icon={<LeftOutlined />}
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          style={{
            position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
            color: '#fff', backgroundColor: 'rgba(0,0,0,0.5)', border: 'none',
            height: 48, width: 48,
          }}
        />
      )}

      <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh', display: 'flex' }}>
        {isVideo ? (
          <video
            src={file.filePath}
            controls
            autoPlay
            style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 4 }}
          />
        ) : (
          <img
            src={file.filePath}
            alt={displayName}
            style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 4 }}
          />
        )}
      </div>

      {files.length > 1 && (
        <Button
          type="text"
          icon={<RightOutlined />}
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          style={{
            position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
            color: '#fff', backgroundColor: 'rgba(0,0,0,0.5)', border: 'none',
            height: 48, width: 48,
          }}
        />
      )}

      {files.length > 1 && (
        <div style={{
          position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          color: '#fff', backgroundColor: 'rgba(0,0,0,0.7)',
          padding: '6px 18px', borderRadius: 20, fontSize: 14,
        }}>
          {currentIndex + 1} / {files.length}
        </div>
      )}
    </div>
  );
};

// ─── Main: EFormViewerBody ────────────────────────────────────────────────────

/**
 * Unified eForm instance viewer body — shared across EFormInstancePage,
 * PendingTasksPage, and WorkflowMonitorPage.
 *
 * Tab 1  "Form Content":  formContentSlot (left) + Received Media (right sidebar)
 * Tab 2  "Form Basic Information":  all meta fields from the instance object
 *
 * Props
 *   instance            – eform instance object
 *   relatedAttachments  – array of media file objects [{fileName, filePath, ...}]
 *   loadingAttachments  – boolean
 *   userTimezoneOffset  – e.g. "UTC+8"
 *   processUserMessage  – optional (userMessage: string) => string | ReactNode
 *   lightboxZIndex      – z-index for the fullscreen lightbox (default 2000)
 *   contentMinHeight    – min-height for the form content area (default 600)
 *   formContentSlot     – React node rendered as the actual form HTML / iframe
 *   isMobile            – boolean; when true the media panel stacks below the form
 */
const EFormViewerBody = ({
  instance,
  relatedAttachments = [],
  loadingAttachments = false,
  userTimezoneOffset = 'UTC+8',
  processUserMessage,
  lightboxZIndex = 2000,
  contentMinHeight = 600,
  formContentSlot,
  isMobile = false,
  // Upload props
  workflowInstanceId,
  workflowStepExecutionId,
  onAttachmentsUploaded,
}) => {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);

  const [lbVisible, setLbVisible]   = useState(false);
  const [lbFiles, setLbFiles]       = useState([]);
  const [lbIndex, setLbIndex]       = useState(0);
  const [uploading, setUploading]   = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const canUpload = !!workflowInstanceId && !!onAttachmentsUploaded;

  const handleUploadFiles = useCallback(async (files) => {
    if (!canUpload || !files || files.length === 0) return;
    const token = localStorage.getItem('token');
    if (!token) { antdMessage.error('Unauthorized'); return; }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    Array.from(files).forEach(f => formData.append('files', f));
    if (workflowStepExecutionId) {
      formData.append('workflowStepExecutionId', String(workflowStepExecutionId));
    }

    try {
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `/api/workflowexecutions/${workflowInstanceId}/media-files`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 95));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.responseText);
          else reject(new Error(xhr.responseText || `HTTP ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(formData);
      });

      setUploadProgress(100);
      antdMessage.success(t('workflowMonitor.mediaUploadSuccess') || 'Files uploaded successfully');
      onAttachmentsUploaded?.();
    } catch (err) {
      antdMessage.error(`${t('workflowMonitor.mediaUploadFailed') || 'Upload failed'}: ${err.message}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [canUpload, workflowInstanceId, workflowStepExecutionId, onAttachmentsUploaded, t]);

  const openLightbox = useCallback((clickedFile, allFiles) => {
    const previewable = (Array.isArray(allFiles) ? allFiles : [clickedFile]).filter(
      (f) => ['image', 'video'].includes(
        getFileType(f.originalFileName || f.name || f.fileName || '')
      )
    );
    const startIdx = previewable.findIndex(
      (f) => f.filePath === clickedFile.filePath
    );
    setLbFiles(previewable);
    setLbIndex(Math.max(0, startIdx));
    setLbVisible(true);
  }, []);

  const closeLightbox = useCallback(() => setLbVisible(false), []);

  const goPrev = useCallback(() => {
    setLbIndex((i) => (i - 1 + lbFiles.length) % lbFiles.length);
  }, [lbFiles.length]);

  const goNext = useCallback(() => {
    setLbIndex((i) => (i + 1) % lbFiles.length);
  }, [lbFiles.length]);

  useEffect(() => {
    const handler = (e) => {
      if (!lbVisible) return;
      if (e.key === 'Escape')      closeLightbox();
      if (e.key === 'ArrowLeft')   goPrev();
      if (e.key === 'ArrowRight')  goNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lbVisible, closeLightbox, goPrev, goNext]);

  const tabItems = [
    {
      key: 'content',
      label: t('workflowMonitor.formContent'),
      children: (
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 0 : 16,
          alignItems: 'flex-start',
        }}>
          {/* ── Left / top: form content ── */}
          <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
            {formContentSlot}
          </div>

          {/* ── Right / bottom: Received Media panel ── */}
          <div style={{
            width: isMobile ? '100%' : 300,
            flexShrink: 0,
            backgroundColor: '#fafafa',
            border: isMobile ? 'none' : '1px solid #f0f0f0',
            borderTop: isMobile ? '1px solid #e8e8e8' : undefined,
            borderRadius: isMobile ? 0 : 8,
            padding: isMobile ? '12px 16px' : '12px',
            marginTop: isMobile ? 12 : 0,
            maxHeight: isMobile ? undefined : contentMinHeight + 80,
            overflowY: isMobile ? undefined : 'auto',
            alignSelf: 'flex-start',
            position: isMobile ? 'static' : 'sticky',
            top: 0,
          }}>
            {/* Panel header */}
            <div style={{
              marginBottom: canUpload ? 6 : 12,
              paddingBottom: 8,
              borderBottom: '1px solid #e8e8e8',
            }}>
              {/* Row 1: title + count */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 14 }}>
                <PictureOutlined />
                <span style={{ whiteSpace: 'nowrap' }}>{t('workflowMonitor.receivedMedia')}</span>
                {!loadingAttachments && relatedAttachments.length > 0 && (
                  <Tag color="blue" style={{ marginLeft: 2, fontSize: 11 }}>
                    {relatedAttachments.length}
                  </Tag>
                )}
              </div>
              {/* Row 2: upload button */}
              {canUpload && (
                <div style={{ marginTop: 6 }}>
                  <Button
                    size="small"
                    icon={uploading ? <LoadingOutlined /> : <UploadOutlined />}
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                    style={{ fontSize: 12, width: '100%' }}
                  >
                    {uploading
                      ? t('workflowMonitor.uploading')
                      : t('workflowMonitor.uploadMedia')}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                    style={{ display: 'none' }}
                    onChange={(e) => handleUploadFiles(e.target.files)}
                  />
                </div>
              )}
            </div>

            {/* Upload progress bar */}
            {uploading && uploadProgress > 0 && (
              <Progress
                percent={uploadProgress}
                size="small"
                status={uploadProgress < 100 ? 'active' : 'success'}
                style={{ marginBottom: 8 }}
              />
            )}

            <ReceivedMediaPanel
              attachments={relatedAttachments}
              loading={loadingAttachments}
              onOpenLightbox={openLightbox}
              t={t}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'info',
      label: t('workflowMonitor.formBasicInfo'),
      children: (
        <FormBasicInfoPanel
          instance={instance}
          userTimezoneOffset={userTimezoneOffset}
          processUserMessage={processUserMessage}
          t={t}
        />
      ),
    },
  ];

  return (
    <div className={isMobile ? 'eform-viewer-mobile' : ''} style={{ width: '100%' }}>
      <Tabs
        defaultActiveKey="content"
        items={tabItems}
        style={isMobile ? { width: '100%' } : undefined}
        tabBarStyle={isMobile ? { paddingLeft: 16, marginBottom: 0 } : undefined}
      />

      <Lightbox
        visible={lbVisible}
        files={lbFiles}
        currentIndex={lbIndex}
        onClose={closeLightbox}
        onPrev={goPrev}
        onNext={goNext}
        zIndex={lightboxZIndex + 10}
      />
    </div>
  );
};

export default EFormViewerBody;
